<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('customer.customers') }}
        </span>
        <h3 class="page-title">
          {{ $t('customer.customersList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/klienci/nowy')">
          <i class="material-icons">add</i>
          {{ $t('customer.addNewCustomer') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="customers-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('customer.enterCustomerData')"
          v-model="filters.search" />

        <v-select
          style="width: 400px;"
          class="ml-2"
          :options="progressOrdersOptions"
          v-model="filters.progressOrders"
          :placeholder="$t('customer.isOrdersInRpogress')"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('customer.companyName') }}</th>
            <th scope="col" class="border-0" style="min-width: 240px;">{{ $t('ui.address') }}</th>
            <th scope="col" class="border-0" style="min-width: 200px;">{{ $t('customer.contact.contact') }}</th>
            <th scope="col" class="border-0" style="min-width: 200px;">{{ $t('customer.statistics') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(customer, index) of customers"
            :key="'customer-' + index">
            <td class="align-middle">
              {{ customer.name }}
              <br v-if="customer.is_supplier">
              <d-badge
                v-if="customer.is_supplier"
                theme="warning">
                {{ $t('ui.supplier') }}
              </d-badge>
            </td>
            <td class="align-middle">
              <small>
                {{ customer.correspondence_company_name }}<br>
                <template v-if="customer.correspondence_name">
                  {{ customer.correspondence_name }}<br>
                </template>
                {{ customer.correspondence_street }} {{ customer.correspondence_building }} <template v-if="customer.correspondence_locale">/ {{ customer.correspondence_locale }}</template><br>
                {{ customer.correspondence_postcode }} {{ customer.correspondence_city }}<br>
                <template v-if="customer.country_name">
                {{ customer.country_name }}
                </template>
              </small>
            </td>
            <td class="align-middle">
              <small>
                {{ $t('customer.phoneShortCap') }}:
                <span
                  v-if="customer.phone"
                  class="text-primary">
                  {{ customer.phone }}
                </span>
                <span
                  v-else
                  class="text-light">
                  {{ $t('ui.lack') }}
                </span>
                <br>
                {{ $t('ui.eMailCap') }}:
                <span
                  v-if="customer.email"
                  class="text-primary">
                  {{ customer.email }}
                </span>
                <span
                  v-else
                  class="text-light">
                  {{ $t('ui.lack') }}
                </span>
              </small>
            </td>
            <td class="align-middle">
              <small>
                {{ $t('customer.ordersAndOffers') }}: {{ customer.orders_count }}<br>
                <template v-if="customer.progress_orders_count === 0">
                  {{ $t('customer.ordersInProgress') }}: {{ customer.progress_orders_count }}<br>
                </template>
                <strong v-else class="text-success">
                  {{ $t('customer.ordersInProgress') }}: {{ customer.progress_orders_count }}<br>
                </strong>
                <template v-if="customer.progress_offers_count === 0">
                  {{ $t('customer.offersInProgress') }}: {{ customer.progress_offers_count }}<br>
                </template>
                <strong v-else class="text-primary">
                  {{ $t('customer.offersInProgress') }}: {{ customer.progress_offers_count }}<br>
                </strong>
                <template v-if="customer.unpaid_settlements_count === 0">
                  {{ $t('customer.unsettledInvoices') }}: {{ customer.unpaid_settlements_count }}
                </template>
                <strong v-else class="text-danger">
                  {{ $t('customer.unsettledInvoices') }}: {{ customer.unpaid_settlements_count }}
                </strong>
              </small>
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/klienci/edycja/' + customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="isAdmin"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeCustomer(customer.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('customer.noCustomersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'customers',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    progressOrdersOptions () {
      return [
        {
          text: this.$t('customer.withoutOrdersInProgress'),
          value: 0
        },
        {
          text: this.$t('customer.withOrdersInProgress'),
          value: 1
        }
      ];
    },
    isAdmin () {
      return this.$store.getters.getParentID === 0;
    }
  },
  data() {
    return {
      customers: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        progressOrders: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.filters.progressOrders !== null && this.filters.progressOrders && typeof this.filters.progressOrders.value === 'number') {
        where.progress_orders = this.filters.progressOrders.value;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/customer-panel/partner-customers/items?page=' + this.currentPage,
        listField: 'customers',
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        progressOrders: ''
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['customers-filters-form'], this.debouncedLoadFilteredData);
    },
    removeCustomer (customerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customer-panel/partner-customers/delete/',
        id: customerID,
        successTitle: this.$t('customer.removeCustomerSuccessTitle'),
        successText: this.$t('customer.removeCustomerSuccessText'),
        confirmTitle: this.$t('customer.removeCustomerConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.removeCustomerErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>

